import { useEffect, useState } from 'react';

/**
 * 断点判定。
 *
 * 只有两种场景需要它：
 *   1. 移动端把侧边导航折叠成抽屉；
 *   2. 单机管理页在窄屏上停用渲染（该页不做移动端适配）。
 * 因此断点集合保持很小，避免出现「每个组件各有一套断点」的混乱。
 */
export const BREAKPOINTS = {
  /** 手机竖屏 */
  sm: 640,
  /** 平板竖屏 / 「桌面端」下限：单机管理页要求 1280 */
  md: 768,
  lg: 1024,
  /** 单机管理页可用的最小宽度 */
  desktop: 1280,
} as const;

export type BreakpointName = 'mobile' | 'tablet' | 'laptop' | 'desktop';

function nameOf(width: number): BreakpointName {
  if (width < BREAKPOINTS.sm) return 'mobile';
  if (width < BREAKPOINTS.lg) return 'tablet';
  if (width < BREAKPOINTS.desktop) return 'laptop';
  return 'desktop';
}

function useViewportWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(window.innerWidth));
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return width;
}

export function useBreakpoint() {
  const width = useViewportWidth();
  const name = nameOf(width);
  return {
    width,
    name,
    isMobile: width < BREAKPOINTS.sm,
    isTablet: width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    /** 单机管理页所需的「足够宽」判定 */
    isWideEnough: width >= BREAKPOINTS.desktop,
  };
}

/**
 * 单机管理页的临时绕过标记。
 *
 * 用 sessionStorage：只在当前标签页有效，关掉即恢复限制，
 * 避免用户误以为手机端已被正式支持。
 */
const BYPASS_KEY = 'havorix.tracking.bypass-mobile-gate';

export function useMobileGateBypass() {
  const [bypassed, setBypassed] = useState(() => {
    try {
      return sessionStorage.getItem(BYPASS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const bypass = () => {
    try {
      sessionStorage.setItem(BYPASS_KEY, '1');
    } catch {
      /* 忽略写入失败，仅本次会话内生效 */
    }
    setBypassed(true);
  };

  const reset = () => {
    try {
      sessionStorage.removeItem(BYPASS_KEY);
    } catch {
      /* 忽略 */
    }
    setBypassed(false);
  };

  return { bypassed, bypass, reset };
}
